import React, { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { formatINR } from "@/components/shared";
import { Printer, ArrowLeft } from "lucide-react";

import { formatDate, formatDateTime } from "@/lib/datetime";
/** Statement print for both customer and franchise ledgers. Route decides via prop. */
export default function LedgerPrint({ payerType }) {
  const { id } = useParams();
  const [sp] = useSearchParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const endpoint = payerType === "customer" ? `/sales/customers/${id}/ledger/print` : `/sales/franchises/${id}/ledger/print`;
        const params = {};
        if (sp.get("date_from")) params.date_from = sp.get("date_from");
        if (sp.get("date_to")) params.date_to = sp.get("date_to");
        const r = await api.get(endpoint, { params });
        setData(r.data);
      } finally { setLoading(false); }
    })();
  }, [id, payerType, sp]);

  useEffect(() => { if (!loading && data) setTimeout(() => window.print(), 400); }, [loading, data]);

  if (loading) return <div className="p-8 text-gray-500">Loading…</div>;
  if (!data) return <div className="p-8 text-red-600">Ledger not found</div>;

  const l = data.ledger;
  const biz = data.business || {};
  const ent = l.entity || {};
  const totalDR = l.entries.reduce((s, e) => s + Number(e.debit || 0), 0);
  const totalCR = l.entries.reduce((s, e) => s + Number(e.credit || 0), 0);

  return (
    <div className="min-h-screen bg-gray-100 print:bg-white">
      <style>{`
        @page { size: A4; margin: 12mm; }
        @media print { .no-print { display: none !important; } body { background: white; } }
      `}</style>
      <div className="no-print sticky top-0 z-10 bg-white border-b border-gray-200 p-3 flex items-center gap-3 shadow-sm">
        <button onClick={() => window.history.back()} className="h-9 w-9 rounded-md border border-gray-200 hover:bg-gray-50 flex items-center justify-center"><ArrowLeft className="h-4 w-4" /></button>
        <span className="font-heading font-semibold text-gray-900">Statement Preview — {ent.name}</span>
        <button onClick={() => window.print()} className="ml-auto h-9 px-3 rounded-md bg-[#9B1C1C] hover:bg-[#7F1D1D] text-white text-sm font-semibold flex items-center gap-1.5" data-testid="ledger-print-do"><Printer className="h-4 w-4" /> Print</button>
      </div>
      <div className="max-w-[210mm] mx-auto bg-white shadow-sm print:shadow-none p-8 my-6 print:my-0 print:p-0 text-gray-900">
        <div className="flex items-start justify-between border-b-2 border-[#9B1C1C] pb-4 mb-6">
          <div>
            {biz.logo_url && <img src={biz.logo_url} alt="Logo" className="h-14 mb-2 object-contain" />}
            <div className="font-heading font-bold text-2xl text-[#9B1C1C]">{biz.business_name || "Thara Gold"}</div>
            <div className="text-xs text-gray-700 mt-1 leading-tight">
              {[biz.address_line1, biz.address_line2].filter(Boolean).join(", ")}<br />
              {[biz.city, biz.state, biz.pincode].filter(Boolean).join(", ")}<br />
              {biz.phone && <>Ph: {biz.phone}</>}
              {biz.gstin && <> · GSTIN {biz.gstin}</>}
            </div>
          </div>
          <div className="text-right">
            <div className="uppercase tracking-widest text-[11px] text-gray-500">Statement of Account</div>
            <div className="font-heading font-bold text-lg text-gray-900 mt-1">{payerType === "customer" ? "Customer" : "Franchise"} Ledger</div>
            <div className="text-xs text-gray-600 mt-1">
              {data.date_from || data.date_to ? `${data.date_from || "beginning"} → ${data.date_to || "today"}` : "All entries"}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-6 mb-5">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-gray-500 mb-1">Account</div>
            <div className="font-semibold text-sm">{ent.name}</div>
            <div className="text-xs text-gray-700 leading-tight">
              {ent.code && <span className="font-mono">{ent.code}</span>}<br />
              {[ent.address_line1, ent.address_line2, ent.city, ent.state, ent.pincode].filter(Boolean).join(", ")}<br />
              {ent.mobile && <>Ph: {ent.mobile}</>}
            </div>
            {ent.gstin && <div className="text-[11px] font-mono text-gray-700 mt-1">GSTIN: {ent.gstin}</div>}
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-gray-500 mb-1">Summary</div>
            <table className="text-xs text-gray-700 w-full">
              <tbody>
                <tr><td className="py-0.5 text-gray-500">Opening Balance</td><td className="py-0.5 text-right tabular-nums">{formatINR(l.opening_balance_in_range)}</td></tr>
                <tr><td className="py-0.5 text-gray-500">Total Debits</td><td className="py-0.5 text-right tabular-nums">{formatINR(totalDR)}</td></tr>
                <tr><td className="py-0.5 text-gray-500">Total Credits</td><td className="py-0.5 text-right tabular-nums text-emerald-700">{formatINR(totalCR)}</td></tr>
                <tr className="border-t border-gray-200"><td className="py-1 font-semibold">Closing Balance</td><td className="py-1 text-right tabular-nums font-heading font-bold text-[#9B1C1C]">{formatINR(l.closing_balance)}</td></tr>
              </tbody>
            </table>
          </div>
        </div>

        <table className="w-full text-xs mb-4 border border-gray-300">
          <thead className="bg-gray-100">
            <tr className="text-left border-b border-gray-300">
              <th className="px-2 py-2 w-20">Date</th>
              <th className="px-2 py-2 w-32">Reference</th>
              <th className="px-2 py-2">Narration</th>
              <th className="px-2 py-2 w-24 text-right">Debit</th>
              <th className="px-2 py-2 w-24 text-right">Credit</th>
              <th className="px-2 py-2 w-24 text-right">Balance</th>
            </tr>
          </thead>
          <tbody>
            {l.entries.map((e, i) => (
              <tr key={i} className="border-b border-gray-200">
                <td className="px-2 py-1.5">{e.date}</td>
                <td className="px-2 py-1.5 font-mono">{e.ref_no}</td>
                <td className="px-2 py-1.5">{e.narration}</td>
                <td className="px-2 py-1.5 text-right tabular-nums">{e.debit ? formatINR(e.debit) : "—"}</td>
                <td className="px-2 py-1.5 text-right tabular-nums">{e.credit ? formatINR(e.credit) : "—"}</td>
                <td className="px-2 py-1.5 text-right tabular-nums font-semibold">{formatINR(e.balance)}</td>
              </tr>
            ))}
            <tr className="bg-gray-50 border-t-2 border-[#9B1C1C]">
              <td colSpan={3} className="px-2 py-2 text-right font-semibold">Totals</td>
              <td className="px-2 py-2 text-right tabular-nums font-semibold">{formatINR(totalDR)}</td>
              <td className="px-2 py-2 text-right tabular-nums font-semibold">{formatINR(totalCR)}</td>
              <td className="px-2 py-2 text-right tabular-nums font-heading font-bold text-[#9B1C1C]">{formatINR(l.closing_balance)}</td>
            </tr>
          </tbody>
        </table>

        {l.outstanding_invoices.length > 0 && (
          <div className="mb-4">
            <div className="text-[10px] uppercase tracking-widest text-gray-500 mb-1">Outstanding Invoices (as of today)</div>
            <table className="w-full text-xs border border-gray-300">
              <thead className="bg-gray-100">
                <tr className="text-left border-b border-gray-300">
                  <th className="px-2 py-1.5">Invoice #</th>
                  <th className="px-2 py-1.5">Date</th>
                  <th className="px-2 py-1.5 text-right">Grand Total</th>
                  <th className="px-2 py-1.5 text-right">Received</th>
                  <th className="px-2 py-1.5 text-right">Outstanding</th>
                </tr>
              </thead>
              <tbody>
                {l.outstanding_invoices.map((inv) => (
                  <tr key={inv.id} className="border-b border-gray-200">
                    <td className="px-2 py-1 font-mono">{inv.invoice_no}</td>
                    <td className="px-2 py-1">{formatDate(inv.invoice_date)}</td>
                    <td className="px-2 py-1 text-right tabular-nums">{formatINR(inv.grand_total)}</td>
                    <td className="px-2 py-1 text-right tabular-nums">{formatINR(inv.allocated)}</td>
                    <td className="px-2 py-1 text-right tabular-nums font-semibold text-red-700">{formatINR(inv.outstanding)}</td>
                  </tr>
                ))}
                <tr className="bg-gray-50">
                  <td colSpan={4} className="px-2 py-1.5 text-right font-semibold">Total Outstanding</td>
                  <td className="px-2 py-1.5 text-right tabular-nums font-heading font-bold text-red-700">{formatINR(l.total_outstanding)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        )}

        <div className="mt-8 text-right">
          <div className="text-[10px] uppercase tracking-widest text-gray-500">For {biz.business_name || "Thara Gold"}</div>
          <div className="border-t border-gray-400 mt-10 pt-1 text-xs font-semibold">Authorised Signatory</div>
        </div>

        {biz.invoice_footer_note && <div className="mt-4 text-center text-xs text-gray-500 italic border-t border-gray-200 pt-2">{biz.invoice_footer_note}</div>}
      </div>
    </div>
  );
}
